/**
 * ECS (GLOSS-74 item 12): size the Infinite Options app's <select>s to their
 * chosen value so the underline sits under the text, not the widest option.
 *
 * The app injects its rows into the buy box after load (see the matching rules
 * in base.css). A native select is as wide as its longest option; browsers
 * with `field-sizing: content` shrink it to the selected option on their own,
 * so this module only steps in where that property is missing: it measures the
 * selected option's text with the select's own font and sets the width, on
 * injection and on every change. The app owns the markup — if it renames its
 * container this module does nothing.
 */
// The PDP container, and the quick add popup's (assets/quick-add.js).
const CONTAINER = ':is(#infiniteoptions-container, .quick-add-modal__line-item-properties)';
const SELECT = `${CONTAINER} select`;

if (!CSS.supports('field-sizing', 'content')) {
  /** @type {HTMLSpanElement | null} */
  let ruler = null;

  /** @param {HTMLSelectElement} select */
  const fit = (select) => {
    const option = select.selectedOptions[0];
    if (!option) return;
    if (!ruler) {
      ruler = document.createElement('span');
      ruler.setAttribute('aria-hidden', 'true');
      ruler.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;inset-block-start:-9999px;';
      document.body.append(ruler);
    }
    const style = getComputedStyle(select);
    ruler.style.font = style.font;
    ruler.style.letterSpacing = style.letterSpacing;
    ruler.textContent = option.textContent ?? '';
    // Room for the text plus the select's own inline padding; a couple of
    // extra pixels keep the last glyph clear of the control's edge.
    const padding = parseFloat(style.paddingInlineStart) + parseFloat(style.paddingInlineEnd);
    select.style.inlineSize = `${Math.ceil(ruler.getBoundingClientRect().width + padding + 2)}px`;
  };

  const fitAll = () => document.querySelectorAll(SELECT).forEach((el) => el instanceof HTMLSelectElement && fit(el));

  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.closest(CONTAINER)) fit(event.target);
  });

  // The app renders asynchronously (and re-renders on variant changes).
  new MutationObserver(fitAll).observe(document.body, { childList: true, subtree: true });
  fitAll();
}
